import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "../components/Navbar";
import Scene from "../components/scene/Scene";
import { useScene } from "../components/scene/SceneContext";
import { getGroup, postGroupSettle } from "../services/AnimalSplitCore";
import { connectGroupSocket } from "../services/SocketService";
import { toggleModal } from "../utils/utils";
import { getStableAnimalEmoji } from "../utils/utils";
import EditTransaction from "./EditTransaction";
import EditContributor from "./EditContributor";
import CreateContributor from "./CreateContributor";
import IslandCanvas from "../components/island/IslandCanvas";
import GroupHero from "../components/group/GroupHero";
import ShareGroup from "./ShareGroup";
import VisitorCenterModal from "./VisitorCenterModal";
import GameCenterModal from "./GameCenterModal";
import SettleConfirmModal from "../components/SettleConfirmModal";
import { MODAL_ID } from "../constants";
import "./GroupIsland.css";

export default function GroupIsland(props) {
  const { setIsLoading } = props;
  const { t, i18n } = useTranslation();
  const { variant } = useScene();
  const { group_id: groupId } = useParams();

  // Defensive cleanup: if a modal was dismissed right before routing here, Bootstrap can
  // occasionally leave a backdrop behind (especially on mobile / slow devices).
  useEffect(() => {
    document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
    document.body.classList.remove("modal-open");
    document.body.style.removeProperty("padding-right");
    document.body.style.removeProperty("overflow");
  }, []);

  const modalId = "island-edit-transaction-modal";
  const contributorModalPrefix = "island-edit-contributor-modal";
  const createContributorModalId = "island-create-contributor-modal";
  const shareGroupModalId = "island-share-group-modal";
  const visitorCenterModalId = "island-visitor-center-modal";
  const gameCenterModalId = "island-game-center-modal";
  const settleConfirmModalId = "island-settle-confirm-modal";

  const [groupName, setGroupName] = useState("");
  const [contributors, setContributors] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [settlements, setSettlements] = useState({});
  const [settlementType, setSettlementType] = useState("");
  const [contributorIdDict, setContributorIdDict] = useState({});
  const [getGroupError, setGetGroupError] = useState(null);
  const [isSettled, setIsSettled] = useState(false);
  const [isSettling, setIsSettling] = useState(false);

  const heroAnnouncementKey = useMemo(() => {
    const id = groupId || "unknown";
    return `animalSplit:group:heroAnnouncements:v1:${id}`;
  }, [groupId]);

  // Collapsed by default; users can expand and we persist the choice per-group.
  const [showHeroAnnouncements, setShowHeroAnnouncements] = useState(false);

  useEffect(() => {
    try {
      const v = localStorage.getItem(heroAnnouncementKey);
      if (v === "0") setShowHeroAnnouncements(false);
      if (v === "1") setShowHeroAnnouncements(true);
    } catch (e) {
      // ignore localStorage failures
    }
  }, [heroAnnouncementKey]);

  const toggleHeroAnnouncements = useCallback(() => {
    setShowHeroAnnouncements((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(heroAnnouncementKey, next ? "1" : "0");
      } catch (e) {
        // ignore localStorage failures
      }
      return next;
    });
  }, [heroAnnouncementKey]);

  const [targetTransaction, setTargetTransaction] = useState(null);
  const [shouldOpenTransactionModal, setShouldOpenTransactionModal] = useState(false);
  const [newTransaction, setNewTransaction] = useState(false);

  const [targetContributorId, setTargetContributorId] = useState(null);
  const [shouldOpenContributorModal, setShouldOpenContributorModal] = useState(false);

  useEffect(() => {
    if (getGroupError) {
      console.error(getGroupError);
      throw getGroupError;
    }
  }, [getGroupError]);

  const renderGroup = useCallback(
    (payload) => {
      const {
        name,
        contributors: contributors_data,
        transactions: transactions_data,
        settlements: settlements_data,
        settlementType: settlement_type_data,
        isSettled: is_settled,
        lang,
      } = payload || {};

      setGroupName(name || "");
      setContributors(Array.isArray(contributors_data) ? contributors_data : []);
      setTransactions(Array.isArray(transactions_data) ? transactions_data : []);
      setSettlements(settlements_data || {});
      setSettlementType(settlement_type_data || "");
      setIsSettled(Boolean(is_settled));

      const dict = {};
      (Array.isArray(contributors_data) ? contributors_data : []).forEach((c) => {
        if (c?.id) dict[c.id] = c;
      });
      setContributorIdDict(dict);

      if (lang) i18n.changeLanguage(lang);
    },
    [i18n]
  );

  const closeModalById = useCallback((modalId) => {
    const el = document.getElementById(modalId);
    if (!el || !window?.bootstrap?.Modal) return;
    try {
      const modal =
        window.bootstrap.Modal.getInstance(el) ||
        window.bootstrap.Modal.getOrCreateInstance(el);
      modal.hide();
    } catch (e) {
      document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove());
      document.body.classList.remove("modal-open");
      document.body.style.removeProperty("padding-right");
      document.body.style.removeProperty("overflow");
    }
  }, []);

  const handleConfirmSettle = useCallback(async () => {
    if (!groupId || isSettled || isSettling) return;
    setIsSettling(true);
    setIsLoading?.(true);
    try {
      const { payload } = await postGroupSettle(groupId);
      renderGroup(payload);
      closeModalById(settleConfirmModalId);
    } catch (e) {
      console.error(e);
      toggleModal(MODAL_ID.GENERIC_ERROR);
    } finally {
      setIsSettling(false);
      setIsLoading?.(false);
    }
  }, [
    groupId,
    isSettled,
    isSettling,
    setIsLoading,
    renderGroup,
    closeModalById,
    settleConfirmModalId,
  ]);

  const refreshGroup = useCallback(
    (id) => {
      if (!id) return;
      setIsLoading?.(true);
      getGroup(id)
        .then(({ payload }) => {
          setIsLoading?.(false);
          renderGroup(payload);
        })
        .catch((error) => {
          setIsLoading?.(false);
          setGetGroupError(error);
        });
    },
    [renderGroup, setIsLoading]
  );

  useEffect(() => {
    refreshGroup(groupId);
  }, [refreshGroup, groupId]);

  useEffect(() => {
    if (!groupId) return;
    const disconnect = connectGroupSocket(groupId, (latestGroup) => {
      renderGroup(latestGroup);
    });
    return () => {
      disconnect?.();
    };
  }, [groupId, renderGroup]);

  const contributorById = useMemo(() => {
    const out = {};
    contributors.forEach((c) => {
      if (c?.id) out[c.id] = c;
    });
    return out;
  }, [contributors]);

  const transactionsWithContributor = useMemo(() => {
    return (Array.isArray(transactions) ? transactions : []).map((tx) => ({
      ...tx,
      contributor: contributorById?.[tx?.contributorId],
    }));
  }, [transactions, contributorById]);

  const heroAnimal2 = useMemo(() => {
    return getStableAnimalEmoji(`friend:${groupId || groupName || "animalsplit"}`);
  }, [groupId, groupName]);

  const handleSelectTransaction = useCallback((tx) => {
    if (!tx) return;
    setNewTransaction(false);
    setTargetTransaction(tx);
    setShouldOpenTransactionModal(true);
  }, []);

  const handleCreateTransaction = useCallback(() => {
    if (isSettled) return;
    setNewTransaction(true);
    setTargetTransaction({});
    setShouldOpenTransactionModal(true);
  }, [isSettled]);

  const handleInviteContributor = useCallback(() => {
    if (isSettled) return;
    window.setTimeout(() => toggleModal(createContributorModalId), 0);
  }, [createContributorModalId, isSettled]);

  const handleSelectContributor = useCallback((contributor) => {
    if (!contributor?.id) return;
    setTargetContributorId(contributor.id);
    setShouldOpenContributorModal(true);
  }, []);

  useEffect(() => {
    if (!shouldOpenTransactionModal) return;
    // Let React flush the EditTransaction prop update before toggling Bootstrap modal.
    window.setTimeout(() => toggleModal(modalId), 0);
    setShouldOpenTransactionModal(false);
  }, [modalId, shouldOpenTransactionModal]);

  useEffect(() => {
    if (!shouldOpenContributorModal) return;
    if (!targetContributorId) return;
    const id = `${contributorModalPrefix}-${targetContributorId}`;
    window.setTimeout(() => toggleModal(id), 0);
    setShouldOpenContributorModal(false);
  }, [contributorModalPrefix, shouldOpenContributorModal, targetContributorId]);

  const editTransactionModal = (
    <EditTransaction
      newTransaction={newTransaction}
      transaction={targetTransaction || {}}
      modalId={modalId}
      groupId={groupId}
      contributors={contributors}
      renderGroup={renderGroup}
      setIsLoading={setIsLoading}
      isSettled={isSettled}
    />
  );

  const createContributorModal = (
    <CreateContributor
      modalId={createContributorModalId}
      groupId={groupId}
      renderGroup={renderGroup}
      setIsLoading={setIsLoading}
    />
  );

  const visitorCenterModal = (
    <VisitorCenterModal
      modalId={visitorCenterModalId}
      groupId={groupId}
      groupName={groupName}
      contributors={contributors}
      renderGroup={renderGroup}
      settlements={settlements}
      contributorIdDict={contributorIdDict}
      settlementType={settlementType}
      setIsLoading={setIsLoading}
      isSettled={isSettled}
      isSettling={isSettling}
      settleConfirmModalId={settleConfirmModalId}
    />
  );

  const gameCenterModal = (
    <GameCenterModal
      modalId={gameCenterModalId}
      groupId={groupId}
      transactions={transactions}
      setIsLoading={setIsLoading}
    />
  );

  const contributorModals = (Array.isArray(contributors) ? contributors : []).map((c) => (
    <EditContributor
      key={c.id}
      modalId={`${contributorModalPrefix}-${c.id}`}
      groupId={groupId}
      contributor={c}
      renderGroup={renderGroup}
      setIsLoading={setIsLoading}
      isSettled={isSettled}
    />
  ));

  const backHref = `${process.env.PUBLIC_URL}/group/${groupId}`;
  const shareGroupModal = (
    <ShareGroup
      modalId={shareGroupModalId}
      groupId={groupId}
      groupName={groupName}
      isSettled={isSettled}
      contributors={contributors}
      settlements={settlements}
      settlementType={settlementType}
      contributorIdDict={contributorIdDict}
      sceneVariant={variant}
    />
  );

  return (
    <div className={`group-island-page as-theme group-island-page--${variant}`}>
      <Scene />
      <div className="group-island-page-content">
        <Navbar
          brandSubtitle={t("group.island") || "Island"}
          shareBtnIconClassName="bi-share-fill"
          showActionBtn={true}
          handleShareBtnOnClick={() => toggleModal(shareGroupModalId)}
        />

        <div className="container-xl">
          <GroupHero
            title={groupName || t("group.loading_title")}
            subtitle={
              <>
                <div className="group-hero-subtitle-row">
                  <div className="group-hero-subtitle-text">
                    {t("group.hero_subtitle")}
                    {" • "}
                    <span className="group-hero-subtitle-cta">
                      {t("group.hero_subtitle_cta") || "💡 Click to see what's new"}
                      <button
                        type="button"
                        className="group-hero-announce-toggle"
                        onClick={toggleHeroAnnouncements}
                        aria-label={
                          showHeroAnnouncements
                            ? (t("group.hide_updates") || "Hide updates")
                            : (t("group.show_updates") || "Show updates")
                        }
                        title={
                          showHeroAnnouncements
                            ? (t("group.hide_updates") || "Hide updates")
                            : (t("group.show_updates") || "Show updates")
                        }
                      >
                        <i className={`bi ${showHeroAnnouncements ? "bi-chevron-up" : "bi-chevron-down"}`} />
                      </button>
                    </span>
                  </div>
                </div>
                {showHeroAnnouncements ? (
                  <div className="group-hero-announcements">
                    <div className="group-hero-announcement">
                      {t("group.settlement_payment_tracking_announcement") ||
                        "✅ New: Mark settlements as paid & pay with Venmo"}
                    </div>
                    <div className="group-hero-announcement">
                      {t("group.hero_subtitle_island_cta") ||
                        "🏝️ New: Animal Island is here — try Island mode!"}
                    </div>
                    <div className="group-hero-announcement">
                      {t("group.share_settlements_announcement") ||
                        "🧾 New: Share settlements (Share → Share settlements)"}
                    </div>
                  </div>
                ) : null}
              </>
            }
            showMascot={false}
            actions={
              <Link
                to={backHref}
                className="btn btn-sm as-btn-soft"
                aria-label={t("group.back_to_list") || "Back"}
                title={t("group.back_to_list") || "Back"}
              >
                <i className="bi bi-arrow-left me-sm-1" />
                <span className="d-none d-sm-inline">
                  {t("group.back_to_list") || "Back"}
                </span>
              </Link>
            }
            meadowIcons={["🌳", "🌿", "🌱", "🐾", heroAnimal2, "🌼", "🌿", "🌱"]}
          />

          <div className="group-island-canvas-card as-card shadow-sm">
            <img
              className="group-island-map-logo"
              src={`${process.env.PUBLIC_URL || ""}/images/logo/animal-island.png`}
              alt=""
              aria-hidden="true"
              draggable="false"
            />
            <IslandCanvas
              contributors={contributors}
              transactions={transactionsWithContributor}
              sceneVariant={variant}
              isSettled={isSettled}
              onSelectTransaction={handleSelectTransaction}
              onSelectContributor={handleSelectContributor}
              onCreateTransaction={handleCreateTransaction}
              onInviteContributor={handleInviteContributor}
              onOpenVisitorCenter={() => toggleModal(visitorCenterModalId)}
              onOpenGameCenter={() => toggleModal(gameCenterModalId)}
            />
          </div>
        </div>
      </div>

      <SettleConfirmModal
        modalId={settleConfirmModalId}
        groupId={groupId}
        groupName={groupName}
        contributors={contributors}
        cancelText={t("general.cancel") || "Cancel"}
        confirmText={
          isSettling
            ? (t("group.settling") || "Settling…")
            : (t("group.settle_confirm_cta") || "Settle now")
        }
        confirmVariantClassName="as-btn-primary-gradient"
        onConfirm={handleConfirmSettle}
        confirmDisabled={!groupId || isSettled || isSettling}
        cancelDisabled={isSettling}
      />

      {typeof document !== "undefined"
        ? createPortal(editTransactionModal, document.body)
        : editTransactionModal}

      {typeof document !== "undefined"
        ? createPortal(<>{contributorModals}</>, document.body)
        : contributorModals}

      {typeof document !== "undefined"
        ? createPortal(createContributorModal, document.body)
        : createContributorModal}

      {typeof document !== "undefined"
        ? createPortal(shareGroupModal, document.body)
        : shareGroupModal}

      {typeof document !== "undefined"
        ? createPortal(visitorCenterModal, document.body)
        : visitorCenterModal}

      {typeof document !== "undefined"
        ? createPortal(gameCenterModal, document.body)
        : gameCenterModal}
    </div>
  );
}

